/** `String`'s parsing behaviour, as the engine defines it. */
import { toInt32, toUint32 } from '../variant/intWidth.js';
/**
 * The spelling `String::is_valid_int()` accepts: an optional sign, then digits. `[+-]?`, not `-?`: Godot reads a leading `+`.
 * Not `to_int()`, which skips non-digits (`ustring.cpp:2267-2301`, {@link stringToInt}): the parse a class uses decides
 * whether a non-numeric index is dropped or lands, so never swap them (see `indexedFamilyValidator`'s `indexParse` option).
 * The source is separate so `godot/classdb/indexedKey.ts` embeds it rather than copy it. No `g` flag: `.test()` stays stateless.
 */
export const IS_VALID_INT_SOURCE = String.raw `[+-]?\d+`;
export const IS_VALID_INT_RE = new RegExp(`^${IS_VALID_INT_SOURCE}$`);
/**
 * One `"…"` string literal as the tokenizer scans it: `\` takes the next character, whatever it is,
 * and the next unescaped `"` ends the string (`variant_parser.cpp:276-290`). A source with no
 * capture group, so a caller embeds it in its own pattern.
 */
export const STRING_LITERAL_SOURCE = String.raw `"(?:[^"\\]|\\[\s\S])*"`;
/**
 * The text inside a serialised `String`, `StringName` or `NodePath` literal. Godot 4 writes a bare StringName with `&` and
 * a bare NodePath with `^` (`variant_parser.cpp`'s `write`), so `&"spin"` and `"walk"` are both plain names to compare.
 * Only a matched outer pair comes off: `" default "` keeps its spaces, because `set_animation` compares the contents.
 * A value that was never quoted comes back trimmed and otherwise untouched.
 */
export function literalText(raw) {
    const bare = raw.trim().replace(/^[&^]/, '');
    const quoted = /^(["'])([\s\S]*)\1$/.exec(bare);
    return quoted ? quoted[2] : bare;
}
/**
 * `text` as Godot writes a `String` value: in quotes, with its backslashes and quotes escaped
 * (`String::c_escape_multiline`, ustring.cpp:4493-4499, written by variant_parser.cpp:2033).
 */
export function stringLiteral(text) {
    return `"${text.replaceAll('\\', '\\\\').replaceAll('"', '\\"')}"`;
}
/**
 * Depth- and quote-aware split of a bracket body's top-level comma-separated elements. Empty input yields no elements.
 * `(`/`[` both open a level and `)`/`]` both close one, because Godot's writer nests the two interchangeably
 * (`Array[NodePath]([NodePath("a"), NodePath("b")])`). Inside a quoted run a backslash escapes the next character.
 */
export function splitTopLevel(body) {
    const trimmed = body.trim();
    if (trimmed === '')
        return [];
    const parts = [];
    let depth = 0;
    let inQuote = false;
    let start = 0;
    for (let i = 0; i < trimmed.length; i++) {
        const c = trimmed[i];
        if (inQuote) {
            if (c === '\\') {
                i++;
                continue;
            }
            if (c === '"')
                inQuote = false;
            continue;
        }
        if (c === '"') {
            inQuote = true;
        }
        else if (c === '(' || c === '[') {
            depth++;
        }
        else if (c === ')' || c === ']') {
            depth--;
        }
        else if (c === ',' && depth === 0) {
            parts.push(trimmed.slice(start, i));
            start = i + 1;
        }
    }
    parts.push(trimmed.slice(start));
    return parts.map((p) => p.trim());
}
/**
 * Drops the empty element a trailing comma leaves. `_parse_array` returns on `]` before it asks
 * for a comma or a value (variant_parser.cpp:1658-1662), so `[1, 2,]` holds 2 elements. Only one
 * trailing empty counts: an interior `,,` fails `parse_value` (:1673-1676). Every bracket-array
 * validator needs this, or it rejects a comma Godot accepts.
 */
export function dropTrailingComma(parts) {
    return parts.length > 1 && parts[parts.length - 1] === '' ? parts.slice(0, -1) : parts;
}
/** The highest code `String::strip_edges` strips: every control character and the space (`ustring.cpp:4076`). */
const STRIPPED_MAX_CODE = 32;
/** `String::strip_edges`: every character up to 32 goes from each end, and nothing else (`ustring.cpp:4070-4099`). */
export function stripEdges(text) {
    let begin = 0;
    let end = text.length;
    while (begin < end && text.charCodeAt(begin) <= STRIPPED_MAX_CODE)
        begin++;
    while (end > begin && text.charCodeAt(end - 1) <= STRIPPED_MAX_CODE)
        end--;
    return text.slice(begin, end);
}
/** A value that is exactly one `"…"` literal and nothing else. No `g` flag: `.test()` stays stateless. */
export const STRING_LITERAL_RE = new RegExp(`^${STRING_LITERAL_SOURCE}$`);
/**
 * The body of each `"…"` element of a comma list, escapes still as written, or null when any
 * element is not one whole literal. An empty list is `[]`. One trailing comma is legal, as in
 * `_parse_array`: the `PackedStringArray(…)` loop also closes on it (variant_parser.cpp:1522-1525).
 */
export function stringLiteralBodies(list) {
    const bodies = [];
    for (const element of dropTrailingComma(splitTopLevel(list))) {
        if (!STRING_LITERAL_RE.test(element))
            return null;
        bodies.push(element.slice(1, -1));
    }
    return bodies;
}
const ZERO = '0'.charCodeAt(0);
const SEVEN = '7'.charCodeAt(0);
const EIGHT = '8'.charCodeAt(0);
const NINE = '9'.charCodeAt(0);
const MINUS = '-'.charCodeAt(0);
const INT64_MAX = (1n << 63n) - 1n;
const INT64_MIN = -(1n << 63n);
/** `INT64_MAX / 10`, the bound `_to_int`'s overflow test compares against (`ustring.cpp:2283`). */
const INT64_MAX_TENTH = INT64_MAX / 10n;
/** `_to_int` tests for overflow only past 18 digits (`if (unlikely(digits > 18))`, `:2282`). */
const UNCHECKED_DIGITS = 18;
/** The digit count is a `uint8_t digits` (`:2275`), so `++digits` wraps to 0 after 255. */
const DIGIT_COUNT_MASK = 0xff;
/**
 * `String::to_int()` (`ustring.cpp:2303-2311`) as the int64 it returns. It skips a character it
 * cannot use (`:2280-2293`), so `"x"` is 0 and `"a1b2"` is 12, and it stops at the first `.`.
 */
function stringToInt64(text) {
    // `if (length() == 0) return 0` (`:2304-2306`).
    if (text.length === 0)
        return 0n;
    const dot = text.indexOf('.');
    const to = dot >= 0 ? dot : text.length;
    let integer = 0n;
    let digits = 0;
    let positive = true;
    for (let i = 0; i < to; i++) {
        const code = text.charCodeAt(i);
        if (code >= ZERO && code <= NINE) {
            if (digits > UNCHECKED_DIGITS) {
                const overflow = integer > INT64_MAX_TENTH ||
                    (integer === INT64_MAX_TENTH && (positive ? code > SEVEN : code > EIGHT));
                if (overflow)
                    return positive ? INT64_MAX : INT64_MIN;
            }
            // `uint64_t integer` (`:2274`): while the wrapped count is 18 or less nothing is tested, and
            // the product wraps modulo 2^64 as the engine's does.
            integer = BigInt.asUintN(64, integer * 10n + BigInt(code - ZERO));
            digits = (digits + 1) & DIGIT_COUNT_MASK;
        }
        else if (integer === 0n && code === MINUS) {
            // A flip, not a leading-sign rule (`:2291-2292`): `"a-1"` is -1, `"--1"` is 1 and `"1-2"` is 12.
            positive = !positive;
        }
    }
    // `int64_t(integer)` and `int64_t(integer * uint64_t(-1))` (`:2296-2300`) wrap modulo 2^64, so a
    // 19-digit value past INT64_MAX, which the overflow test never sees, comes back negative.
    return BigInt.asIntN(64, positive ? integer : -integer);
}
/** A clean spelling of at most 15 digits: `to_int` reads its value, which a double holds. */
const EXACT_DOUBLE_INT_RE = /^[+-]?\d{1,15}$/;
/**
 * `String::to_int()` as the `int` or `uint32_t` a caller stores it in. Each indexed `_set` does
 * `int which = ….to_int()` (`property_list_helper.cpp:57`, `bone_twist_disperser_3d.cpp:37`), and
 * `skeleton_3d.cpp:82` a `uint32_t`. The narrowing keeps the low 32 bits, so `4294967296` is 0 and
 * `2147483648` is -2147483648. Always a safe integer, never NaN.
 */
export function stringToInt(text, width = 'int32') {
    if (EXACT_DOUBLE_INT_RE.test(text)) {
        // Both narrowings are exact below 2^53, and each maps `-0` to the engine's one zero.
        const value = Number(text);
        return width === 'int32' ? toInt32(value) : toUint32(value);
    }
    // int64 to `int` is implementation-defined before C++20: GCC, at `-std=gnu++17`
    // (SConstruct:891), keeps it modulo 2^32. To `uint32_t` it is modular by the standard.
    const value = stringToInt64(text);
    return Number(width === 'int32' ? BigInt.asIntN(32, value) : BigInt.asUintN(32, value));
}
/**
 * The body of a `res://` path: every character `String::is_valid_filename` allows in a file name,
 * with `/` kept as the separator (`core/string/ustring.cpp:4927`). A space and a parenthesis stay in.
 * A source without anchors, so a caller embeds it.
 */
export const RES_PATH_BODY_SOURCE = String.raw `[^:\\?*"|%<>]+`;
/**
 * `String::simplify_path()` (`core/string/ustring.cpp:4152-4210`) for the `scheme://` form of every `.tscn` resource address.
 * Godot splits the drive (ASCII alphanumerics then `://`) off the front and rebuilds the rest from its non-empty parts, so a
 * run of slashes collapses to one and `res:///addons/...` resolves. It leaves `.`/`..` alone: removing `..` without the
 * drive's own root would change which file a path names.
 */
export function simplifyResPath(path) {
    const separator = path.indexOf('://');
    // `p > 0` plus the all-alphanumeric check (`:4159-4167`): `://x` has no
    // drive at all, and neither does a scheme carrying punctuation.
    if (separator <= 0 || !/^[A-Za-z0-9]+$/.test(path.slice(0, separator)))
        return path;
    const drive = path.slice(0, separator + 3);
    const rest = path.slice(separator + 3);
    return (drive +
        rest
            .split('/')
            .filter((part) => part !== '')
            .join('/'));
}
/**
 * `String::to_float()` (`core/string/ustring.cpp:2680-2685`): an empty string is 0, and `built_in_strtod` takes the longest
 * numeric prefix, or 0. Not {@link parseGodotFloat}, which reads an anchored TSCN float literal because unread text corrupts
 * the next assignment. `to_float` is the lenient scan a class applies to text it holds (a BBCode tag option, a split
 * component), so trailing text is ignored. Swapping the two changes what a malformed value reads as.
 */
export function stringToFloat(text) {
    // `built_in_strtod` skips leading whitespace, then takes the longest prefix it can read. `parseFloat` has the same
    // prefix rule and 0-length miss, and differs only in reading JavaScript's `Infinity`, which the finite check maps to 0.
    const trimmed = text.trimStart();
    const parsed = Number.parseFloat(trimmed);
    return Number.isFinite(parsed) ? parsed : 0;
}
/**
 * `path.get_file().get_basename()` (`ustring.cpp:5028-5035`, `:5136-5143`): the name after the last
 * `/` or `\`, less its last extension.
 */
export function fileBaseName(path) {
    const file = path.slice(Math.max(path.lastIndexOf('/'), path.lastIndexOf('\\')) + 1);
    const dot = file.lastIndexOf('.');
    return dot < 0 ? file : file.slice(0, dot);
}
/** The characters `String::validate_filename` replaces (`ustring.cpp:4927`). */
const INVALID_FILENAME_CHARACTERS = /[:/\\?*"|%<>]/g;
/** `String::validate_filename` (`ustring.cpp:4947-4953`): stripped edges, each invalid character an underscore. */
export function validateFilename(name) {
    return stripEdges(name).replace(INVALID_FILENAME_CHARACTERS, '_');
}
/**
 * `String::get_base_dir` (`ustring.cpp:4967-5026`): the path up to its last `/` or `\`, keeping the
 * root a scheme (`res://`), a drive (`C:/`), a network share (`//server/share/`) or `/` names.
 */
export function getBaseDir(path) {
    const rootEnd = pathRootEnd(path);
    const base = path.slice(0, rootEnd);
    const rest = path.slice(rootEnd);
    const separator = Math.max(rest.lastIndexOf('/'), rest.lastIndexOf('\\'));
    return separator === -1 ? base : base + rest.slice(0, separator);
}
/** Where the root of `path` ends, as `get_base_dir` finds it (`ustring.cpp:4968-5009`), or 0. */
function pathRootEnd(path) {
    const scheme = path.indexOf('://');
    if (scheme !== -1)
        return scheme + 3;
    const drive = indexOfEither(path, ':/', ':\\');
    if (drive !== -1)
        return drive + 2;
    if (path.startsWith('//') || path.startsWith('\\\\'))
        return networkShareRootEnd(path);
    return path.startsWith('/') ? 1 : 0;
}
/** The end of a `//server/share/` root, or 0 when the share has no closing separator (`:4988-5002`). */
function networkShareRootEnd(path) {
    const server = indexOfEither(path, '/', '\\', 2);
    const share = indexOfEither(path, '/', '\\', server + 1);
    return share === -1 ? 0 : share + 1;
}
/** The first `first` from `from`, else the first `second`, as `get_base_dir` searches. */
function indexOfEither(path, first, second, from = 0) {
    const found = path.indexOf(first, from);
    return found === -1 ? path.indexOf(second, from) : found;
}
/** `String::path_join` (`ustring.cpp:5046-5054`): one `/` between the two, unless one side has it. */
export function pathJoin(base, file) {
    if (base === '')
        return file;
    return base.endsWith('/') || file.startsWith('/') ? base + file : `${base}/${file}`;
}
//# sourceMappingURL=string.js.map